import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Card, CardContent } from '../../../../components/ui/Card.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/FormField.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Money } from '../../../../components/ui/Money';
import { EntityActionMenu } from '../../../../components/ui/EntityActionMenu';
import { Search, Plus, HeartHandshake, FileMinus, ShieldAlert } from 'lucide-react';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';

import { useWriteOffs } from '../../../../features/finance/hooks';
import { TableSkeleton } from '../../../../components/ui/Skeleton';
import { EmptyState } from '../../../../components/ui/EmptyState';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';

export const Route = createFileRoute('/_auth/finance/write-offs/')({
  component: WriteOffsPage,
});

interface WriteOffRecord {
  id: string;
  subscriberId: string | null;
  subscriberName: string | null;
  ticketNumber: number | null;
  schemeCode: string | null;
  amountPaise: number;
  recoveredPaise: number;
  writeOffDate: string;
  status: 'WRITTEN_OFF' | 'RECOVERING' | 'RECOVERED';
}

const STATUS_LABEL: Record<WriteOffRecord['status'], string> = {
  WRITTEN_OFF: 'WRITTEN OFF',
  RECOVERING: 'PARTIALLY RECOVERED',
  RECOVERED: 'FULLY RECOVERED',
};

function WriteOffsPage() {
  usePageTitle('Bad Debts');
  const [query, setQuery] = React.useState('');
  const navigate = useNavigate();
  const { data: response, isLoading, isError, error, refetch } = useWriteOffs(query ? { query } : undefined);
  const records = (response?.data ?? []) as WriteOffRecord[];

  const filteredRecords = records.filter(r =>
    (r.subscriberName ?? '').toLowerCase().includes(query.toLowerCase()) ||
    String(r.ticketNumber ?? '').toLowerCase().includes(query.toLowerCase()) ||
    (r.schemeCode ?? '').toLowerCase().includes(query.toLowerCase())
  );

  const totalPaiseWrittenOff = records.reduce((acc, r) => acc + r.amountPaise, 0);
  const totalPaiseRecovered = records.reduce((acc, r) => acc + r.recoveredPaise, 0);
  const totalPaiseOutstanding = totalPaiseWrittenOff - totalPaiseRecovered;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Bad Debts & Write-offs"
        subtitle="Provision and audit defaulted subscriber accounts marked as unrecoverable, and track subsequent recovery filings."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/finance/write-offs/new' })}>
            <Plus className="w-4 h-4 mr-2" />
            Provision Write-off
          </Button>
        }
      />
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-text-muted" />
          <Input
            type="text"
            placeholder="Search defaulted subscriber, ticket..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full h-9 pl-9 pr-3"
          />
        </div>
      </div>
      <div className="flex-1 w-full min-h-0">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Written-off Provision</span>
                <h2 className="text-2xl font-bold text-text-primary">
                  <Money paise={totalPaiseWrittenOff} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </h2>
              </div>
              <div className="p-3 bg-surface-3 text-text-secondary rounded-lg border border-border">
                <FileMinus className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Total Subsequent Recoveries</span>
                <h2 className="text-2xl font-bold text-success-text">
                  <Money paise={totalPaiseRecovered} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </h2>
              </div>
              <div className="p-3 bg-success-bg text-success-500 rounded-lg">
                <HeartHandshake className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="space-y-1">
                <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Net Outstanding Loss</span>
                <h2 className="text-2xl font-bold text-danger-600">
                  <Money paise={totalPaiseOutstanding} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                </h2>
              </div>
              <div className="p-3 bg-danger-bg text-danger-text rounded-lg">
                <ShieldAlert className="w-6 h-6" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={8} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load write-offs</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filteredRecords.length === 0 ? (
            <EmptyState
              title="No write-offs found"
              description={query ? 'No records match your search.' : 'No bad debt write-offs have been provisioned yet.'}
              icon={<FileMinus className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Defaulting Subscriber</th>
                  <th className="p-4">Disputed Scheme</th>
                  <th className="p-4">Written-off Principal</th>
                  <th className="p-4">Recovered So Far</th>
                  <th className="p-4">Outstanding Bal</th>
                  <th className="p-4">Write-off Date</th>
                  <th className="p-4">Recovery Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredRecords.map(r => {
                  const outstanding = r.amountPaise - r.recoveredPaise;
                  return (
                    <tr key={r.id} className="hover:bg-surface-2 transition-colors">
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-text-primary">{r.subscriberName ?? 'Unlinked'}</span>
                          <span className="font-mono text-xs text-text-muted mt-0.5">{r.ticketNumber ?? '—'}</span>
                        </div>
                      </td>
                      <td className="p-4 font-mono text-xs">{r.schemeCode ?? '—'}</td>
                      <td className="p-4">
                        <Money paise={r.amountPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4 text-success-text font-semibold">
                        <Money paise={r.recoveredPaise} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4 font-bold text-danger-600">
                        <Money paise={outstanding} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} />
                      </td>
                      <td className="p-4 font-mono text-xs">{r.writeOffDate}</td>
                      <td className="p-4">
                        <StatusBadge
                          status={r.status === 'RECOVERED' ? 'success' : r.status === 'RECOVERING' ? 'warning' : 'danger'}
                          label={STATUS_LABEL[r.status]}
                        />
                      </td>
                      <td className="p-4 text-right">
                        <EntityActionMenu
                          actions={[
                            {
                              label: 'Log Recovery',
                              icon: <HeartHandshake className="w-4 h-4" />,
                              hidden: r.status === 'RECOVERED',
                              onClick: () => {
                                navigate({ to: `/finance/write-offs/$id/recover`, params: { id: r.id } });
                              },
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
